import { useMemo, useRef, useState } from "react";
import { DecisionTypes, PreparationStatusColorMap, REQUEST_STATUS_COLOR_MAP, STATUS_COLOR_MAP } from "src/common/constants";
import ReportsPagesContent from "src/component/ReportsPagesContent";
import SearchWrapper from "src/component/searchSection";
import { GetRequests } from "src/requests/Requests";
import { GetStatusLookup } from "src/requests/RequestStatus";
import { CheckIFNullable, checkIfSelectedPharmacy, dateFormatter, errorToaster, getMonthNumber, } from "src/utils";
import { Original } from "src/common/Common.Type";
import { useTranslation } from "react-i18next";
import MedicinesPopup from "./MedicinesPopup";
import { RowAction } from "src/component/RequestRowAction/inde";
import { usePrintImages } from "src/hooks/usePrintImages";
import { PatientData } from "src/types/patientData";
import Status from "src/component/Status/Status";
import PickupCard from "./PickupCard";

import RequestNavTabs from "./RequestNavTabs/RequestNavTabs";
import PrintRequest from "src/component/PrintedPDF/PrintRequestTable";
import { Alert } from "src/component/Alert";

import "./style.css";
import BatchDispensePopup from "src/component/popup/BatchDispensePopup/BatchDispensePopup";
import BaseBtn from "src/component/ActionBtn/BaseBtn";
import { useLocation } from "react-router-dom";

export const REQUESTS_TABS = {
    All: 1,
    Preparing: 3,
    preparedAndReadyForDelivery: 2,
    Expired: 4,
}

const Requests = () => {
    const { t } = useTranslation()
    const { handlePrint } = usePrintImages({ title: "request-print" })
    const [remainingAmount, setRemainingAmount] = useState(0)
    const [mothlyAmount, setMothlyAmount] = useState(0)
    const [disabledTabs, setDisabledTabs] = useState<number[]>([])
    const AccordionRef = useRef<any>("")

    const [recordID, setRecordID] = useState<number | null>(null)
    const [requestIdToViewBatches, setRequestIdToViewBatches] = useState<number | null>(null)
    const [hospitalID, setHospitalID] = useState<number | null>(null)
    const [decisionId, setDecisionId] = useState<number | null>(null)
    const [patientData, setPatientData] = useState<PatientData | null>(null)
    const [selectedTab, setSelectedTab] = useState(REQUESTS_TABS.All)

    const [selectedPrintRequestId, setSelectedPrintRequestId] = useState<number | null>(null)
    const { state } = useLocation()
    const printRequestRef = useRef<HTMLDivElement>(null);
    const printRequestIsNeedCoolingMedicines = useRef<HTMLDivElement>(null);
    const batchDispensePopupRef = useRef<HTMLDivElement>(null);

    const columns = useMemo(() => [
        {
            Header: "requestId",
            accessor: "requestId",
            canSearch: true,
            Cell: ({ row }: any) => RowAction(row, (ObjectData: any) =>
                ShowAccordion(ObjectData?.id, ObjectData.hospitalId, row?.original?.remainingAmount, row?.original?.monthlyAmount, row?.original?.decisionId, {
                    name: ObjectData?.patientName,
                    nationalId: ObjectData?.patientNationalId,
                    decisionNumber: ObjectData?.decisionNumber,
                    requestNumber: ObjectData?.id,
                }), handlePrintModel, false, "", (selectedTab !== REQUESTS_TABS.Expired))
        },
        {
            Header: "decisionId",
            accessor: "decisionNumber",
        },
        {
            Header: "patientName",
            accessor: "patientName",
            label: "patientNameOrNationalId",
            canSearch: true
        },
        {
            Header: "phoneNumber",
            accessor: "phoneNumber",
        },
        {
            Header: "nationalId",
            accessor: "patientNationalId",
        },
        {
            Header: "status",
            accessor: "status",
            Cell: ({ row: { original } }: Original) => {
                return (
                    <Status cellColorMap={REQUEST_STATUS_COLOR_MAP} cellContent={original.statusId} >
                        {original.status ? original.status : "-"}
                    </Status>)
            }
        },
        {
            Header: "hospitalName",
            accessor: "hospitalName",
        },
        {
            Header: "shippingCompanyName",
            accessor: "shippingCompanyName",
        },
        {
            Header: "requestDueDate",
            accessor: "requestDueDate",
            Cell: ({ row: { original } }: Original) => <> {dateFormatter(original?.requestDueDate, "en")}</>

        },
        {
            Header: "packedDate",
            Cell: ({ row: { original } }: Original) => {
                return (
                    <>
                        {CheckIFNullable(original.packedDate)
                            ? "-" : dateFormatter(original?.packedDate, "en")
                        }
                    </>
                )
            }
        },
        {
            Header: "lastDeliveryDate",
            Cell: ({ row: { original } }: Original) => (dateFormatter(original.lastDeliveryDate, "en")),
        },
        {
            Header: "preparationStatus",
            Cell: ({ row: { original } }: Original) => {
                return (
                    <Status cellColorMap={PreparationStatusColorMap} cellContent={original.preparationStatusId} >
                        {original.preparationStatus ? original.preparationStatus : "-"}
                    </Status>)
            }
        },
        {
            Header: "diagnosis",
            accessor: "diagnosis",
        },
        {
            Header: "decisionTypeCol",
            accessor: "decisionType",
            Cell: ({ row: { original } }: Original) => {
                return (
                    <Status cellContent={original?.decisionTypeId} cellColorMap={STATUS_COLOR_MAP} >
                        {t(original?.decisionType)}
                    </Status>
                )
            }
        },
        {
            Header: "decisionPeriodRange",
            Cell: ({ row: { original } }: Original) => {
                return dateFormatter(original?.createdAt, "ar", {
                    month: "long",
                    year: "numeric",
                }
                )
            }
        },
        {
            Header: "serviceType",
            accessor: "serviceType",
        },
        {
            Header: "decisionPeriod",
            Cell: ({ row: { original } }: Original) => {
                return original?.decisionTypeId === DecisionTypes.Normal ? (
                    <>
                        {dateFormatter(original?.decisionStartDate, "ar", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        })}
                        ⬅️
                        {dateFormatter(original?.decisionEndDate, "ar", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        })}
                    </>
                ) : (
                    "-"
                );
            }
        },
        {
            Header: "monthNumber",
            Cell: ({ row: { original } }: Original) => original?.decisionTypeId === DecisionTypes.Normal ? getMonthNumber(original?.decisionStartDate) : "-"
        },

        {
            Header: "statusId",
            accessor: "statusId",
            type: "DropDown",
            canSearch: true,
            visible: false,
            sourceApi: GetStatusLookup
        },
        {
            Header: "requestCreatedDate",
            accessor: "requestCreatedDate",
            canSearch: true,
            visible: false,
            type: "Date",
            isMulti: true,
        },
        {
            Header: "actions",
            Cell: ({ row: { original } }: any) => {
                return (
                    <>
                        {/* <BaseBtn
                            label="printRequest"
                            icon={"material-symbols:print-rounded"}
                            className='update'
                            ApplyAction={() => handlePrintModel(original.id)}
                            isVisible={original?.isPendingRequest}
                        /> */}
                        <BaseBtn
                            label="BatchDispensePopupBtn"
                            icon={"jam:inboxes-f"}
                            className='update'
                            ApplyAction={() => {
                                setRequestIdToViewBatches(original.id)
                                batchDispensePopupRef?.current?.click()
                            }}
                            isVisible={!original?.isPendingRequest}
                        />
                    </>
                )
            }
        }
    ], [selectedTab, setSelectedPrintRequestId, state])



    const ShowAccordion = (id: number, hopsitalId: number, remainingAmount: number, monthlyAmount: number, decisionId: number, patientData: PatientData) => {

        if (!checkIfSelectedPharmacy(state)) return errorToaster("please-select-hosptial")
        AccordionRef?.current?.click()
        setRecordID(id)
        setRemainingAmount(remainingAmount)
        setMothlyAmount(monthlyAmount)
        setDecisionId(decisionId)
        setHospitalID(hopsitalId)
        setPatientData({
            name: patientData?.name,
            nationalId: patientData?.nationalId,
            decisionNumber: patientData?.decisionNumber,
            requestNumber: patientData?.requestNumber,
        })
    }

    const handlePrintModel = (id: number) => {
        setSelectedPrintRequestId(id)
    }
    return (
        <>
            <BatchDispensePopup
                modalId={batchDispensePopupRef}
                recordId={requestIdToViewBatches}
                handlePrint={() => setSelectedPrintRequestId(requestIdToViewBatches)}
            />

            <PrintRequest
                printRefs={[printRequestRef, printRequestIsNeedCoolingMedicines]}
                selectedPrintRequestId={selectedPrintRequestId}
                handlePrint={handlePrint}
                setSelectedPrintRequestId={setSelectedPrintRequestId}
            />

            <MedicinesPopup
                remainingAmount={remainingAmount}
                mothlyAmount={mothlyAmount}
                recordID={recordID}
                hospitalId={hospitalID}
                modalID={AccordionRef}
                decisionId={decisionId}
                patientData={patientData}
                setRecordID={setRecordID}
            />

            <ReportsPagesContent
                columns={columns}
                needParams
                tableAPI={
                    (searchData: any, tableFiller: any) => GetRequests(searchData, tableFiller, Number(sessionStorage.getItem("hopsitalId")), selectedTab)}
            >
                <SearchWrapper haveCustomPriority >

                    <PickupCard
                        setSelectedTab={setSelectedTab}
                        selectedTab={selectedTab}
                        setDisabledTabs={setDisabledTabs}
                    />
                </SearchWrapper>
                <RequestNavTabs
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    disabledTabs={disabledTabs}
                />
                {selectedTab === REQUESTS_TABS.Expired &&
                    <Alert variant="danger" className="mb-3 mt-0 text-center" >
                        {t("expiredRequestsWarningText")}
                    </Alert>}

            </ReportsPagesContent>
        </>
    )
}

export default Requests